/**
 * Print only the insights content.
 *
 * Before snapshotting we pin the content column to a fixed width so every
 * chart re-renders at the same size regardless of the current window width —
 * the printed report no longer depends on whether the browser was wide or
 * narrow when the user hit print. The fixed-size content is then cloned into
 * an isolated iframe (so the app shell — sidebar, header, action bar — never
 * reaches the print output) and printed from the iframe.
 */

// A4 content column width (210mm − 2×12mm page margins ≈ 700px at 96dpi),
// widened a touch to absorb the section-card padding so charts land near the
// printable width.
const FIXED_CONTENT_WIDTH_PX = 740;

export const printInsightsView = () => {
  const root = document.querySelector(".insights-print-root");
  if (!root) {
    window.print();
    return;
  }

  root.classList.add("insights-print-prepping");

  // The content column whose width we pin so every chart re-renders at the same
  // size. `.insights-print-root` is that column in the current layout; the
  // legacy `#insights-scroll` id is kept as a first choice for older layouts.
  const scroll = document.getElementById("insights-scroll") || root;
  const prevScrollStyle = scroll.style.cssText;
  scroll.style.flex = "none";
  scroll.style.width = `${FIXED_CONTENT_WIDTH_PX}px`;
  scroll.style.minWidth = `${FIXED_CONTENT_WIDTH_PX}px`;

  // SimpleGrid picks its column count from a viewport media query, not the
  // parent width, so side-by-side charts (e.g. Top speakers / Top sponsors)
  // stay two-up and render half-width even after the column is pinned. Collapse
  // every chart-bearing grid to one column so each chart re-renders full width
  // — matching the single-column layout the print stylesheet already enforces.
  const chartGrids = Array.from(
    scroll.querySelectorAll(".mantine-SimpleGrid-root")
  ).filter((g) =>
    g.querySelector(".mantine-BarChart-root, .mantine-AreaChart-root")
  );
  const prevGridStyles = chartGrids.map((g) => g.style.cssText);
  chartGrids.forEach((g) => {
    g.style.gridTemplateColumns = "1fr";
  });

  const restore = () => {
    root.classList.remove("insights-print-prepping");
    scroll.style.cssText = prevScrollStyle;
    chartGrids.forEach((g, i) => {
      g.style.cssText = prevGridStyles[i];
    });
  };

  // Give Recharts' ResizeObserver a frame plus a short beat to re-render every
  // chart against the pinned width before we snapshot it.
  requestAnimationFrame(() =>
    setTimeout(() => snapshotAndPrint(root, restore), 150)
  );
};

const snapshotAndPrint = (root, restore) => {
  let iframe;
  try {
    const styleNodes = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((node) => node.outerHTML)
      .join("\n");

    iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const clone = root.cloneNode(true);
    const headerEl = clone.querySelector(".insights-print-header");
    const headerHtml = headerEl ? headerEl.outerHTML : "";
    if (headerEl) headerEl.remove();
    const bodyHtml = clone.innerHTML;

    // Mantine's scheme-dependent color vars (e.g. --mantine-color-orange-text,
    // --mantine-color-orange-light) are only defined under the
    // [data-mantine-color-scheme] selector, so the iframe's <html> must carry
    // it or KPI value colors and icon tints resolve to nothing.
    const colorScheme =
      document.documentElement.getAttribute("data-mantine-color-scheme") ||
      "light";

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html>
    <html data-mantine-color-scheme="${colorScheme}">
      <head>
        <meta charset="utf-8" />
        <title>Insights report</title>
        ${styleNodes}
        <style>
          body { margin: 0; padding: 0; background: white; }
          table.insights-print-table {
            width: 100%;
            border-collapse: collapse;
          }
          table.insights-print-table thead { display: table-header-group; }
          table.insights-print-table tbody { display: table-row-group; }
          table.insights-print-table td,
          table.insights-print-table th { padding: 0; }
        </style>
      </head>
      <body>
        <div class="insights-print-root">
          <table class="insights-print-table">
            <thead><tr><th>${headerHtml}</th></tr></thead>
            <tbody><tr><td>${bodyHtml}</td></tr></tbody>
          </table>
        </div>
      </body>
    </html>`);
    doc.close();

    const cleanup = () => {
      restore();
      setTimeout(() => iframe.remove(), 300);
    };

    // Charts are now rendered at the fixed column width, so a chart wider than
    // the printable area shrinks by the same factor on every run. `zoom` is
    // applied on the chart element (not the whole card) so the legend and SVG
    // plot scale together and stay aligned. We measure the original chart in the
    // live document (the iframe is 0x0 so its scrollWidth is unreliable), then
    // apply the matching zoom to the cloned chart in the iframe.
    const fitChartsToPage = () => {
      // A4 width (~794px) minus 12mm + 12mm margins (~91px) → safe ~680px.
      const PAGE_CONTENT_WIDTH_PX = 680;
      // Charts keep `break-inside: avoid`, so a tall chart that doesn't fit
      // the remaining space gets pushed to the next page, leaving a large
      // blank gap. Capping the printed height keeps that gap small.
      const MAX_CHART_HEIGHT_PX = 400;
      // Zoom the whole chart element, not just the SVG wrapper. Mantine sets the
      // fixed height (`h`) on `.mantine-*Chart-root`; zooming only the inner
      // `.recharts-wrapper` left that reserved height in place, so a height-capped
      // chart printed with a large empty gap below it. Scaling the root collapses
      // the reserved height together with the content.
      const chartSelector = ".mantine-AreaChart-root, .mantine-BarChart-root";
      const sourceCharts = root.querySelectorAll(chartSelector);
      const cloneRoot = doc.querySelector(".insights-print-root");
      const cloneCharts = cloneRoot
        ? cloneRoot.querySelectorAll(chartSelector)
        : [];
      sourceCharts.forEach((src, i) => {
        const target = cloneCharts[i];
        if (!target) return;
        const rect = src.getBoundingClientRect();
        const w = rect.width || parseFloat(src.style.width) || src.scrollWidth;
        const h =
          rect.height || parseFloat(src.style.height) || src.scrollHeight;
        const zoom = Math.min(
          1,
          w ? PAGE_CONTENT_WIDTH_PX / w : 1,
          h ? MAX_CHART_HEIGHT_PX / h : 1
        );
        if (zoom < 1) target.style.zoom = zoom;
      });
    };

    let printed = false;
    const startPrint = () => {
      if (printed) return;
      printed = true;
      try {
        fitChartsToPage();
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        cleanup();
      }
    };

    if (doc.readyState === "complete") {
      setTimeout(startPrint, 200);
    } else {
      iframe.onload = () => setTimeout(startPrint, 200);
      // Fallback: if onload never fires, still print (or at least restore) so
      // the page can't stay pinned at the fixed width indefinitely.
      setTimeout(startPrint, 1500);
    }
  } catch (err) {
    // A throw during iframe/doc setup would otherwise leave the page pinned at
    // the fixed width with collapsed grids and no recovery. Restore first, then
    // surface the error.
    restore();
    if (iframe) iframe.remove();
    throw err;
  }
};
